import React, { useMemo, useState } from 'react';
import { AbsoluteFill, OffthreadVideo, useCurrentFrame, useVideoConfig } from 'remotion';
import qrcode from 'qrcode-generator';
import type { CtaScene, FeaturesScene, ImageScene, QuoteScene, Scene, ScreenshotScene, StatScene, StatementScene, TitleScene, VideoScene } from '../types';
import { Brand, Check, Eyebrow, Heading, ImagePlaceholder, Rise, SafeImg, SceneCtx, SceneFrame, useLayout } from '../ui';
import { FONTS, alpha, isDark, onColor } from '../theme';
import { lerp, pop, prog } from '../anim';
import { Overlays } from '../overlays';
import { SpeechCaptions } from '../captions';

type P<S> = { scene: S; ctx: SceneCtx; first?: boolean };

const Title: React.FC<P<TitleScene>> = ({ scene, ctx, first }) => {
    const frame = useCurrentFrame();
    const { u, portrait } = useLayout();
    const t = ctx.spec.theme;
    const line = prog(frame, 40, 24);
    return (
        <SceneFrame ctx={ctx} first={first}>
            <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: `0 ${90 * u}px` }}>
                <Rise at={2}><Brand spec={ctx.spec} size={44} /></Rise>
                {scene.eyebrow && <Rise at={12} style={{ marginTop: 48 * u }}><Eyebrow color={t.accent}>{scene.eyebrow}</Eyebrow></Rise>}
                <Rise at={20} dist={50} style={{ marginTop: 24 * u }}>
                    <Heading spec={ctx.spec} size={portrait ? 120 : 140}>{scene.title}</Heading>
                </Rise>
                <div style={{ marginTop: 22 * u, height: 10 * u, borderRadius: 99, background: t.accent, width: `${line * (portrait ? 60 : 36)}%` }} />
                {scene.subtitle && (
                    <Rise at={34} style={{ marginTop: 36 * u, maxWidth: 1300 * u }}>
                        <div style={{ fontSize: 40 * u, lineHeight: 1.4, opacity: 0.78, fontWeight: 500 }}>{scene.subtitle}</div>
                    </Rise>
                )}
            </AbsoluteFill>
        </SceneFrame>
    );
};

const Statement: React.FC<P<StatementScene>> = ({ scene, ctx, first }) => {
    const { u, portrait } = useLayout();
    const t = ctx.spec.theme;
    const parts = scene.emphasis && scene.text.includes(scene.emphasis) ? scene.text.split(scene.emphasis) : null;
    return (
        <SceneFrame ctx={ctx} first={first}>
            <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: `0 ${110 * u}px` }}>
                <Rise at={4} dist={40}>
                    <Heading spec={ctx.spec} size={portrait ? 104 : 118}>
                        {parts ? (
                            <>{parts[0]}<span style={{ color: t.accent, fontStyle: t.headingFont === 'serif' ? 'italic' : 'normal' }}>{scene.emphasis}</span>{parts.slice(1).join(scene.emphasis)}</>
                        ) : scene.text}
                    </Heading>
                </Rise>
            </AbsoluteFill>
        </SceneFrame>
    );
};

const BrowserFrame: React.FC<{ url?: string; children: React.ReactNode; style?: React.CSSProperties }> = ({ url, children, style }) => {
    const { u } = useLayout();
    return (
        <div style={{ borderRadius: 24 * u, overflow: 'hidden', background: '#fff', border: `${2 * u}px solid rgba(0,0,0,0.08)`, boxShadow: `0 ${60 * u}px ${110 * u}px -${50 * u}px rgba(0,0,0,0.45)`, display: 'flex', flexDirection: 'column', ...style }}>
            <div style={{ height: 48 * u, display: 'flex', alignItems: 'center', gap: 9 * u, padding: `0 ${18 * u}px`, background: '#F4F4F1', borderBottom: '1px solid rgba(0,0,0,0.07)', flex: 'none' }}>
                {[0, 1, 2].map((i) => <i key={i} style={{ width: 12 * u, height: 12 * u, borderRadius: 99, background: '#D6D5CC', display: 'block' }} />)}
                <div style={{ flex: 1, marginLeft: 12 * u, height: 28 * u, borderRadius: 99, background: '#EAEAE5', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONTS.mono, fontSize: 16 * u, color: '#6E7274' }}>{url || ''}</div>
            </div>
            <div style={{ position: 'relative', flex: 1, overflow: 'hidden' }}>{children}</div>
        </div>
    );
};

const Screenshot: React.FC<P<ScreenshotScene>> = ({ scene, ctx, first }) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const { u, portrait, square } = useLayout();
    const t = ctx.spec.theme;
    const s = pop(frame, fps, 2, 16);
    const scroll = prog(frame, 20, Math.max(30, ctx.frames - 40));
    // Screenshots are desktop-shaped: fill the frame in landscape, keep a 16:10
    // window (centered) in portrait/square so nothing gets cropped sideways.
    const tall = portrait || square;
    return (
        <SceneFrame ctx={ctx} first={first}>
            <AbsoluteFill style={{ padding: tall ? `0 ${50 * u}px` : `${70 * u}px ${150 * u}px ${170 * u}px`, justifyContent: 'center', transform: `translateY(${(1 - s) * 80 * u}px)`, opacity: s }}>
                <BrowserFrame url={scene.url} style={tall ? { width: '100%', aspectRatio: '16 / 10.5' } : { width: '100%', height: '100%' }}>
                    <SafeImg
                        src={scene.imageUrl}
                        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', objectPosition: `50% ${lerp(scroll, 0, 40)}%` }}
                        fallback={<ImagePlaceholder color={t.primary} />}
                    />
                </BrowserFrame>
            </AbsoluteFill>
            {scene.caption && (
                <Rise at={14} style={{ position: 'absolute', left: tall ? 50 * u : 150 * u, right: tall ? 50 * u : undefined, bottom: portrait ? 330 * u : square ? 70 * u : 56 * u }}>
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 16 * u, padding: `${16 * u}px ${30 * u}px`, borderRadius: 99, background: t.primary, color: onColor(t.primary), fontSize: 40 * u, fontWeight: 700 }}>{scene.caption}</div>
                </Rise>
            )}
        </SceneFrame>
    );
};

const ImageS: React.FC<P<ImageScene>> = ({ scene, ctx, first }) => {
    const frame = useCurrentFrame();
    const { u } = useLayout();
    const kb = prog(frame, 0, ctx.frames);
    return (
        <SceneFrame ctx={ctx} first={first} bg="#000">
            <SafeImg src={scene.imageUrl} style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', transform: `scale(${lerp(kb, 1.04, 1.16)})` }} fallback={<ImagePlaceholder color={ctx.spec.theme.primary} />} />
            <AbsoluteFill style={{ background: 'linear-gradient(180deg, rgba(0,0,0,0) 35%, rgba(0,0,0,0.78) 100%)' }} />
            <AbsoluteFill style={{ justifyContent: 'flex-end', padding: `${90 * u}px`, color: '#fff' }}>
                {scene.title && <Rise at={8}><Heading spec={ctx.spec} size={96}>{scene.title}</Heading></Rise>}
                {scene.caption && <Rise at={18} style={{ marginTop: 18 * u }}><div style={{ fontSize: 38 * u, opacity: 0.88, maxWidth: 1300 * u, lineHeight: 1.4 }}>{scene.caption}</div></Rise>}
            </AbsoluteFill>
        </SceneFrame>
    );
};

const Clip: React.FC<P<VideoScene>> = ({ scene, ctx, first }) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const long = ctx.frames > fps * 8;
    const titleOpacity = long ? 1 - prog(frame, fps * 4, 12) : 1;
    const speech = !!(ctx.spec.captions?.enabled && scene.muted === false && scene.subtitles?.length);
    const titleTop = speech || !!(ctx.spec.captions?.enabled && ctx.spec.voice.enabled && scene.narration);
    const { u } = useLayout();
    const [broken, setBroken] = useState(false);
    return (
        <SceneFrame ctx={ctx} first={first} bg="#000">
            {broken ? (
                <ImagePlaceholder color={ctx.spec.theme.primary} />
            ) : (
                <OffthreadVideo
                    src={scene.videoUrl}
                    trimBefore={Math.max(0, Math.round((scene.trimStart ?? 0) * fps))}
                    muted={scene.muted !== false}
                    onError={() => setBroken(true)}
                    style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover' }}
                />
            )}
            {(scene.title || scene.caption) && (
                // On long footage the title is an intro, not a permanent banner. With
                // narration captions at the bottom, the title moves to the top.
                <AbsoluteFill style={{ opacity: titleOpacity }}>
                    <AbsoluteFill style={{ background: titleTop ? 'linear-gradient(0deg, rgba(0,0,0,0) 55%, rgba(0,0,0,0.7) 100%)' : 'linear-gradient(180deg, rgba(0,0,0,0) 45%, rgba(0,0,0,0.75) 100%)' }} />
                    <AbsoluteFill style={{ justifyContent: titleTop ? 'flex-start' : 'flex-end', padding: `${90 * u}px`, color: '#fff' }}>
                        {scene.title && <Rise at={8}><Heading spec={ctx.spec} size={92}>{scene.title}</Heading></Rise>}
                        {scene.caption && <Rise at={18} style={{ marginTop: 16 * u }}><div style={{ fontSize: 36 * u, opacity: 0.9, maxWidth: 1300 * u, lineHeight: 1.4 }}>{scene.caption}</div></Rise>}
                    </AbsoluteFill>
                </AbsoluteFill>
            )}
            {scene.overlays?.length ? (
                <Overlays overlays={scene.overlays} spec={ctx.spec} sourceAspect={scene.sourceAspect ?? 16 / 9} sceneFrames={ctx.frames} />
            ) : null}
            {speech && <SpeechCaptions segments={scene.subtitles!} trimStart={scene.trimStart ?? 0} />}
        </SceneFrame>
    );
};

const Features: React.FC<P<FeaturesScene>> = ({ scene, ctx, first }) => {
    const { u, portrait } = useLayout();
    const t = ctx.spec.theme;
    const dark = isDark(t.background);
    return (
        <SceneFrame ctx={ctx} first={first}>
            <AbsoluteFill style={{ justifyContent: 'center', padding: `0 ${(portrait ? 70 : 150) * u}px` }}>
                <Rise at={2}><Heading spec={ctx.spec} size={portrait ? 92 : 96}>{scene.title}</Heading></Rise>
                <div style={{ marginTop: 50 * u, display: 'grid', gridTemplateColumns: portrait || scene.items.length <= 3 ? '1fr' : '1fr 1fr', gap: 22 * u }}>
                    {scene.items.map((item, i) => (
                        <Rise key={i} at={14 + i * 8} dist={24}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 22 * u, padding: `${24 * u}px ${30 * u}px`, borderRadius: 24 * u, background: dark ? 'rgba(255,255,255,0.07)' : '#FFFFFF', border: `${2 * u}px solid ${dark ? 'rgba(255,255,255,0.12)' : alpha(t.ink, 0.08)}`, fontSize: 40 * u, fontWeight: 600 }}>
                                <Check size={46 * u} bg={t.accent} fg={onColor(t.accent)} />
                                {item}
                            </div>
                        </Rise>
                    ))}
                </div>
            </AbsoluteFill>
        </SceneFrame>
    );
};

const Stat: React.FC<P<StatScene>> = ({ scene, ctx, first }) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const { u, width } = useLayout();
    const t = ctx.spec.theme;
    const s = pop(frame, fps, 4, 12);
    // Fit long values ("Rp1.050.000") on narrow canvases: ~0.6 em per glyph.
    const valueSize = Math.min(300 * u, (width - 180 * u) / (Math.max(scene.value.length, 3) * 0.6));
    // Count up the leading number, keep any prefix/suffix ("Rp 1.000", "400+").
    const m = scene.value.match(/^(\D*)(\d[\d.,]*)(.*)$/);
    let value = scene.value;
    if (m) {
        const target = parseInt(m[2].replace(/[.,]/g, ''), 10);
        const n = Math.round(target * prog(frame, 4, 36));
        value = `${m[1]}${n.toLocaleString('id-ID')}${m[3]}`;
    }
    return (
        <SceneFrame ctx={ctx} first={first} bg={t.primary}>
            <AbsoluteFill style={{ alignItems: 'center', justifyContent: 'center', textAlign: 'center', color: onColor(t.primary), padding: `0 ${90 * u}px` }}>
                <div style={{ fontSize: valueSize, fontWeight: 800, letterSpacing: '-0.05em', lineHeight: 0.9, whiteSpace: 'nowrap', transform: `scale(${0.8 + 0.2 * s})`, opacity: s }}>{value}</div>
                <Rise at={16} style={{ marginTop: 30 * u }}><Heading spec={ctx.spec} size={70}>{scene.label}</Heading></Rise>
                {scene.sub && <Rise at={26} style={{ marginTop: 18 * u }}><div style={{ fontSize: 36 * u, opacity: 0.8 }}>{scene.sub}</div></Rise>}
            </AbsoluteFill>
        </SceneFrame>
    );
};

const Quote: React.FC<P<QuoteScene>> = ({ scene, ctx, first }) => {
    const { u } = useLayout();
    const t = ctx.spec.theme;
    return (
        <SceneFrame ctx={ctx} first={first}>
            <AbsoluteFill style={{ justifyContent: 'center', padding: `0 ${140 * u}px` }}>
                <Rise at={0}><div style={{ fontFamily: FONTS.serif, fontSize: 260 * u, lineHeight: 0.6, color: t.accent }}>“</div></Rise>
                <Rise at={8} dist={40}><div style={{ fontFamily: FONTS.serif, fontSize: 76 * u, lineHeight: 1.15, letterSpacing: '-0.015em' }}>{scene.quote}</div></Rise>
                <Rise at={24} style={{ marginTop: 44 * u }}>
                    <div style={{ fontSize: 36 * u, fontWeight: 700 }}>{scene.author}</div>
                    {scene.role && <div style={{ fontFamily: FONTS.mono, fontSize: 22 * u, letterSpacing: '0.16em', textTransform: 'uppercase', opacity: 0.6, marginTop: 8 * u }}>{scene.role}</div>}
                </Rise>
            </AbsoluteFill>
        </SceneFrame>
    );
};

const Qr: React.FC<{ text: string; size: number }> = ({ text, size }) => {
    const svg = useMemo(() => {
        const qr = qrcode(0, 'M');
        qr.addData(text);
        qr.make();
        return qr.createSvgTag({ cellSize: 4, margin: 0, scalable: true }).replace('<svg ', '<svg style="width:100%;height:100%;display:block" ');
    }, [text]);
    return <div style={{ width: size, height: size, padding: size * 0.08, borderRadius: size * 0.09, background: '#fff' }} dangerouslySetInnerHTML={{ __html: svg }} />;
};

const Cta: React.FC<P<CtaScene>> = ({ scene, ctx, first }) => {
    const frame = useCurrentFrame();
    const { fps } = useVideoConfig();
    const { u, portrait, square } = useLayout();
    const stacked = portrait || square;
    const t = ctx.spec.theme;
    const fg = onColor(t.primary);
    const card = pop(frame, fps, 24, 14);
    const glow = 0.5 + 0.5 * Math.sin(frame / 9);
    const href = /^https?:\/\//i.test(scene.url) ? scene.url : `https://${scene.url}`;
    return (
        <SceneFrame ctx={ctx} first={first} bg={t.primary}>
            <AbsoluteFill style={{ flexDirection: stacked ? 'column' : 'row', alignItems: 'center', justifyContent: 'center', gap: (square ? 40 : 90) * u, padding: `0 ${(square ? 70 : 120) * u}px`, color: fg, textAlign: stacked ? 'center' : 'left' }}>
                <div style={{ flex: stacked ? 'none' : 1, display: 'flex', flexDirection: 'column', alignItems: stacked ? 'center' : 'flex-start' }}>
                    <Rise at={0}><Brand spec={ctx.spec} size={40} color={fg} /></Rise>
                    <Rise at={8} dist={50} style={{ marginTop: (square ? 24 : 44) * u }}><Heading spec={ctx.spec} size={square ? 92 : 120}>{scene.title}</Heading></Rise>
                    {scene.subtitle && <Rise at={18} style={{ marginTop: 24 * u }}><div style={{ fontSize: (square ? 34 : 40) * u, opacity: 0.85, lineHeight: 1.35, maxWidth: 1000 * u }}>{scene.subtitle}</div></Rise>}
                    <Rise at={30} style={{ marginTop: (square ? 30 : 50) * u }}>
                        <div style={{ display: 'inline-flex', alignItems: 'center', height: 96 * u, padding: `0 ${50 * u}px`, borderRadius: 99, background: t.accent, color: onColor(t.accent), fontWeight: 800, fontSize: 42 * u, boxShadow: `0 0 0 ${12 * glow * u}px ${alpha(t.accent, 0.25)}` }}>{scene.buttonText}</div>
                    </Rise>
                    <Rise at={38} style={{ marginTop: 22 * u }}><div style={{ fontFamily: FONTS.mono, fontSize: 26 * u, opacity: 0.8 }}>{scene.url.replace(/^https?:\/\//, '')}</div></Rise>
                </div>
                {scene.showQr !== false && (
                    <div style={{ transform: `scale(${card}) rotate(${(1 - card) * 6}deg)`, opacity: card, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20 * u }}>
                        <Qr text={href} size={(portrait ? 420 : square ? 220 : 380) * u} />
                        <div style={{ fontFamily: FONTS.mono, fontSize: 20 * u, letterSpacing: '0.2em', textTransform: 'uppercase', opacity: 0.75 }}>Scan untuk membuka</div>
                    </div>
                )}
            </AbsoluteFill>
        </SceneFrame>
    );
};

export const SceneView: React.FC<{ scene: Scene; ctx: SceneCtx; first?: boolean }> = ({ scene, ctx, first }) => {
    switch (scene.type) {
        case 'title': return <Title scene={scene} ctx={ctx} first={first} />;
        case 'statement': return <Statement scene={scene} ctx={ctx} first={first} />;
        case 'screenshot': return <Screenshot scene={scene} ctx={ctx} first={first} />;
        case 'image': return <ImageS scene={scene} ctx={ctx} first={first} />;
        case 'features': return <Features scene={scene} ctx={ctx} first={first} />;
        case 'stat': return <Stat scene={scene} ctx={ctx} first={first} />;
        case 'quote': return <Quote scene={scene} ctx={ctx} first={first} />;
        case 'video': return <Clip scene={scene} ctx={ctx} first={first} />;
        case 'cta': return <Cta scene={scene} ctx={ctx} first={first} />;
        default: return null;
    }
};
